import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bot, CheckCircle2, ChevronLeft, ChevronRight, Database, ExternalLink, Search,
  ShieldCheck, Trash2, UserRound, Users,
} from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";


const safeAvatarUrl = (value) => {
  if (!value) return null;
  try {
    const host = new URL(value).hostname.toLowerCase();
    return host.endsWith("cdninstagram.com")
      || host.endsWith("fbcdn.net")
      || host.endsWith("instagram.com")
      || host.endsWith("idrivee2.com") ? value : null;
  } catch { return null; }
};


const ProfileAvatar = ({ profile, large = false }) => {
  const [failed, setFailed] = useState(false);
  const source = safeAvatarUrl(profile.profile_pic_hd_url);
  return <span className={`avatar ${large ? "large" : ""}`} data-testid={`profile-avatar-${profile.pk}`}>
    {source && !failed
      ? <img src={source} alt={`Foto de ${profile.username}`} onError={() => setFailed(true)} data-testid={`profile-avatar-image-${profile.pk}`} />
      : <UserRound size={large ? 25 : 18} aria-hidden="true" />}
  </span>;
};


const ProfileSkeletons = () => Array.from({ length: 8 }, (_, index) => (
  <div className="table-row profile-grid profile-skeleton" key={index} data-testid={`profile-skeleton-${index}`}>
    <span className="profile-cell"><Skeleton className="h-16 w-16 rounded-full" /><Skeleton className="h-10 w-36" /></span>
    <Skeleton className="h-4 w-24" /><Skeleton className="h-4 w-20" /><Skeleton className="h-4 w-20" /><span />
  </div>
));


const metricConfig = [
  ["profiles_total", "Perfis capturados", Users, "profiles-metric-total"],
  ["pool_available", "Credenciais disponíveis", Database, "profiles-metric-pool"],
  ["pool_invalid", "Itens indisponíveis", ShieldCheck, "profiles-metric-invalid"],
];


export default function ProfilesPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState("");
  const [cursors, setCursors] = useState([null]);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteJobId, setDeleteJobId] = useState(null);
  const notifiedJob = useRef(null);
  const cursor = cursors[cursors.length - 1];
  const currentPage = cursors.length;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["profiles", applied, cursor],
    queryFn: () => api.get("/profiles", {
      params: { search: applied || undefined, cursor: cursor || undefined, limit: 25 },
    }).then((response) => response.data),
  });
  const { data: dashboard, isLoading: dashboardLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get("/dashboard").then((response) => response.data),
    refetchInterval: 5000,
  });
  const { data: deleteJob } = useQuery({
    queryKey: ["profile-delete-job", deleteJobId],
    queryFn: () => api.get(`/profiles/delete-jobs/${deleteJobId}`).then((response) => response.data),
    enabled: Boolean(deleteJobId),
    refetchInterval: (query) => ["queued", "running"].includes(query.state.data?.status) ? 1000 : false,
  });

  useEffect(() => {
    if (!deleteJob || notifiedJob.current === `${deleteJob.id}:${deleteJob.status}`) return;
    if (deleteJob.status === "completed") {
      notifiedJob.current = `${deleteJob.id}:${deleteJob.status}`;
      toast.success("Todos os perfis foram excluídos.");
      setCursors([null]);
      queryClient.invalidateQueries({ queryKey: ["profiles"] });
    }
    if (deleteJob.status === "failed") {
      notifiedJob.current = `${deleteJob.id}:${deleteJob.status}`;
      toast.error("A exclusão em lote encontrou um erro.");
    }
  }, [deleteJob, queryClient]);

  const loading = isLoading || isFetching;
  const showPagination = currentPage > 1 || data?.has_more;
  const jobPercent = deleteJob?.total
    ? Math.min(100, ((deleteJob.processed || 0) / deleteJob.total) * 100)
    : deleteJob?.status === "completed" ? 100 : 0;

  const submit = (event) => {
    event.preventDefault();
    setCursors([null]);
    setApplied(search.trim());
  };
  const openDetails = (event, profile) => {
    if (event.type === "keydown" && !["Enter", " "].includes(event.key)) return;
    if (event.type === "keydown") event.preventDefault();
    setSelected(profile);
  };
  const requestDelete = (mode, profile = null) => {
    setSelected(null);
    setDeletePassword("");
    setDeleteTarget({ mode, profile });
  };
  const confirmDelete = async () => {
    if (!deletePassword) return;
    setDeleting(true);
    try {
      if (deleteTarget.mode === "all") {
        const response = await api.post("/profiles/delete-all", { password: deletePassword });
        setDeleteJobId(response.data.id);
        notifiedJob.current = null;
        toast.success("Exclusão em lote iniciada.");
      } else {
        await api.delete(`/profiles/${deleteTarget.profile.pk}`, { data: { password: deletePassword } });
        toast.success("Perfil e imagem excluídos.");
        queryClient.invalidateQueries({ queryKey: ["profiles"] });
      }
      setDeleteTarget(null);
      setDeletePassword("");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  return <div className="page">
    {dashboardLoading ? <section className="metrics-grid profiles-metrics" data-testid="profiles-metrics-loading"><Skeleton className="h-32" /><Skeleton className="h-32" /><Skeleton className="h-32" /><Skeleton className="h-32" /></section> : <section className="metrics-grid profiles-metrics" data-testid="profiles-metrics">
      {metricConfig.map(([key, label, Icon, testId]) => <article className="metric" key={key} data-testid={testId}><div className="metric-top"><span>{label}</span><Icon size={19} /></div><strong data-testid={`${testId}-value`}>{Number(dashboard?.[key] || 0).toLocaleString("pt-BR")}</strong><small>Atualizado agora</small></article>)}
      <article className="metric" data-testid="profiles-metric-bot"><div className="metric-top"><span>Status do bot</span><Bot size={19} /></div><StatusBadge status={dashboard?.bot?.status || "idle"} testId="profiles-metric-bot-status" /><small data-testid="profiles-metric-bot-id">ID atual: {dashboard?.bot?.current_id || "—"}</small></article>
    </section>}
    <form className="search-bar" onSubmit={submit} data-testid="profiles-search-form">
      <Search size={18} />
      <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por username, PK ou nome" data-testid="profiles-search-input" />
      <Button type="submit" data-testid="profiles-search-button">Buscar</Button>
      <Button type="button" size="icon" variant="destructive" onClick={() => requestDelete("all")} data-testid="profiles-delete-all-button" aria-label="Excluir todos os perfis" title="Excluir todos"><Trash2 size={17} /></Button>
    </form>

    {deleteJobId && <section className="delete-progress" data-testid="profiles-delete-progress">
      <div><strong>{deleteJob?.status === "completed" ? "Exclusão concluída" : "Excluindo perfis"}</strong><span data-testid="profiles-delete-progress-count">{Number(deleteJob?.processed || 0).toLocaleString("pt-BR")} / {Number(deleteJob?.total || 0).toLocaleString("pt-BR")}</span></div>
      <div className="progress-line"><span style={{ width: `${jobPercent}%` }} /></div>
    </section>}

    <section className="data-table-wrap profiles-table-wrap" data-testid="profiles-table">
      <div className="table-head profile-grid"><span>PERFIL</span><span>PK</span><span>STATUS</span><span>CAPTURADO</span><span /></div>
      {loading ? <ProfileSkeletons /> : data?.items?.length ? data.items.map((profile) => (
        <div className="table-row profile-grid" role="button" tabIndex={0} key={profile.pk} onClick={(event) => openDetails(event, profile)} onKeyDown={(event) => openDetails(event, profile)} data-testid={`profile-row-${profile.pk}`}>
          <span className="profile-cell"><ProfileAvatar profile={profile} /><span><strong data-testid={`profile-username-${profile.pk}`}>@{profile.username}</strong><small data-testid={`profile-name-${profile.pk}`}>{profile.full_name || "Sem nome"}</small></span></span>
          <span data-testid={`profile-pk-${profile.pk}`} className="mono">{profile.pk}</span>
          <span>{profile.is_verified ? <span className="verified"><CheckCircle2 size={15} /> Verificado</span> : <span className="muted">Padrão</span>}</span>
          <span data-testid={`profile-date-${profile.pk}`}>{profile.captured_at ? new Date(profile.captured_at).toLocaleDateString("pt-BR") : "—"}</span>
          <a className="profile-instagram-link" href={`https://www.instagram.com/${encodeURIComponent(profile.username)}/`} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()} data-testid={`profile-instagram-link-${profile.pk}`} aria-label={`Abrir @${profile.username} no Instagram`} title="Abrir no Instagram"><ExternalLink size={17} /></a>
        </div>
      )) : <p className="table-empty" data-testid="profiles-empty">Nenhum perfil encontrado.</p>}
    </section>

    {showPagination && <div className="pagination-row" data-testid="profiles-pagination">
      <Button size="icon" variant="outline" onClick={() => setCursors((items) => items.length > 1 ? items.slice(0, -1) : items)} disabled={currentPage === 1 || loading} data-testid="profiles-previous-page-button" aria-label="Página anterior" title="Página anterior"><ChevronLeft size={17} /></Button>
      <span className="page-indicator" data-testid="profiles-current-page">{currentPage}</span>
      <Button size="icon" variant="outline" onClick={() => data?.next_cursor && setCursors((items) => [...items, data.next_cursor])} disabled={!data?.has_more || loading} data-testid="profiles-next-page-button" aria-label="Próxima página" title="Próxima página"><ChevronRight size={17} /></Button>
    </div>}

    <Dialog open={Boolean(selected)} onOpenChange={() => setSelected(null)}>
      <DialogContent data-testid="profile-detail-dialog"><DialogHeader><DialogTitle>Detalhes do perfil</DialogTitle><DialogDescription>Dados essenciais capturados e identificador de origem.</DialogDescription></DialogHeader>{selected && <div className="profile-detail"><div className="profile-hero"><ProfileAvatar profile={selected} large /><div><h3 data-testid="profile-detail-name">{selected.full_name || "Sem nome"}</h3><p data-testid="profile-detail-username">@{selected.username}</p></div></div><dl><div><dt>PK</dt><dd>{selected.pk}</dd></div><div><dt>Verificado</dt><dd>{selected.is_verified ? "Sim" : "Não"}</dd></div><div><dt>ID de origem</dt><dd>{selected.source_target_id || "—"}</dd></div></dl><DialogFooter><Button variant="destructive" onClick={() => requestDelete("one", selected)} data-testid="profile-delete-button"><Trash2 size={16} />Excluir perfil</Button></DialogFooter></div>}</DialogContent>
    </Dialog>

    <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
      <DialogContent data-testid="profile-delete-dialog"><DialogHeader><DialogTitle>{deleteTarget?.mode === "all" ? "Excluir todos os perfis" : "Excluir perfil"}</DialogTitle><DialogDescription>Digite a senha de exclusão para confirmar. As imagens também serão removidas do IDrive.</DialogDescription></DialogHeader><Input type="password" value={deletePassword} onChange={(event) => setDeletePassword(event.target.value)} placeholder="Senha de exclusão" data-testid="profile-delete-password-input" /><DialogFooter><Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="profile-delete-cancel-button">Cancelar</Button><Button variant="destructive" onClick={confirmDelete} disabled={deleting || !deletePassword} data-testid="profile-delete-confirm-button">{deleting ? "Excluindo..." : "Confirmar exclusão"}</Button></DialogFooter></DialogContent>
    </Dialog>
  </div>;
}